/**
 * Heuristics + helpers for AI-powered resume section UX in Account Settings.
 */

import type { OnboardingDraft } from './types';

export type SectionHealthTone = 'strong' | 'optimized' | 'warning' | 'empty';

export interface SectionHealth {
  tone: SectionHealthTone;
  label: string;
}

/** Dispatched after Account Settings switches to Resume tab to expand + scroll a section. */
export const RESUME_SECTION_FOCUS_EVENT = 'ggj:focus-resume-section' as const;

export interface ResumeSectionFocusDetail {
  sectionKey: string;
}

const METRICS_RE =
  /\d+%|\$\d|₹\d|\d+\+|\d+\s*(years|months|weeks|days|people|users|customers|clients|projects|k|m\b)/i;

export function contentHasMetrics(text: string): boolean {
  return METRICS_RE.test(text);
}

function parseSkillTokens(content: string): string[] {
  const skills: string[] = [];
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.indexOf(':');
    const skillPart =
      colonIdx > 0 ? trimmed.slice(colonIdx + 1).replace(/^:?\s*/, '') : trimmed;
    for (const chunk of skillPart.split(',')) {
      const s = chunk.trim();
      if (s.length > 1 && s.length < 60) skills.push(s);
    }
  }
  return [...new Set(skills)];
}

export function getSectionHealth(section: { title: string; content: string }): SectionHealth {
  const lower = section.title.toLowerCase();
  const content = (section.content || '').trim();

  if (!content) {
    return { tone: 'empty', label: 'Empty' };
  }

  if (
    lower.includes('summary') ||
    lower.includes('objective') ||
    (lower.includes('professional') && !lower.includes('experience'))
  ) {
    if (content.length >= 120 && contentHasMetrics(content)) {
      return { tone: 'strong', label: 'Strong' };
    }
    if (content.length >= 80) return { tone: 'optimized', label: 'Good' };
    return { tone: 'warning', label: 'Too Short' };
  }

  if (lower.includes('experience') || lower.includes('work') || lower.includes('employment')) {
    if (!contentHasMetrics(content)) {
      return { tone: 'warning', label: 'Missing Metrics' };
    }
    return { tone: 'strong', label: 'Strong' };
  }

  if (lower.includes('skill')) {
    const count = parseSkillTokens(content).length;
    if (count >= 5) return { tone: 'optimized', label: 'Optimized' };
    if (count >= 3) return { tone: 'strong', label: 'Good' };
    return { tone: 'warning', label: 'Add More' };
  }

  if (lower.includes('education') || lower.includes('certif') || lower.includes('language')) {
    return { tone: 'optimized', label: 'Good' };
  }

  return { tone: 'optimized', label: 'Good' };
}

export function getSectionAiTip(section: { title: string; content: string }): string | null {
  const lower = section.title.toLowerCase();
  const content = (section.content || '').trim();

  if (!content) {
    if (lower.includes('summary') || lower.includes('objective')) {
      return 'Start with 2–3 sentences highlighting your top achievement and target role.';
    }
    if (lower.includes('experience') || lower.includes('work')) {
      return 'Add at least one role with 3–5 impact bullets to unlock stronger job matches.';
    }
    if (lower.includes('skill')) {
      return 'Aim for 5–8 skills aligned with your target role for better ATS visibility.';
    }
    return null;
  }

  if (
    (lower.includes('summary') || lower.includes('objective')) &&
    !contentHasMetrics(content)
  ) {
    return 'Add metrics (e.g., %, $, or timelines) to your summary to increase visibility score by up to 14%.';
  }

  if (
    (lower.includes('experience') || lower.includes('work')) &&
    !contentHasMetrics(content)
  ) {
    return 'Add metrics (e.g., %, $, or timelines) to this section to increase your visibility score by up to 14%.';
  }

  if (lower.includes('skill')) {
    const count = parseSkillTokens(content).length;
    if (count < 5) {
      return 'Profiles with 5+ targeted skills rank higher in recruiter searches — see AI recommendations below.';
    }
  }

  if (content.length < 60 && !lower.includes('skill')) {
    return 'Expand this section with specific outcomes — recruiters scan for quantified impact first.';
  }

  return null;
}

export function completionKeyToSectionMatcher(key: string): (title: string) => boolean {
  switch (key) {
    case 'summary':
      return (t) => {
        const l = t.toLowerCase();
        return l.includes('summary') || l.includes('objective') || l.includes('profile');
      };
    case 'workExp':
      return (t) => {
        const l = t.toLowerCase();
        return l.includes('experience') || l.includes('work') || l.includes('employment');
      };
    case 'education':
      return (t) => t.toLowerCase().includes('education');
    case 'certOrLang':
      return (t) => {
        const l = t.toLowerCase();
        return l.includes('certif') || l.includes('language');
      };
    default:
      return () => false;
  }
}

export function findSectionIndexForKey(
  sections: { title: string }[],
  key: string,
): number {
  const match = completionKeyToSectionMatcher(key);
  return sections.findIndex((s) => match(s.title));
}

const ROLE_SKILL_POOLS: Record<string, string[]> = {
  engineer: ['TypeScript', 'System Design', 'CI/CD', 'Docker', 'AWS'],
  developer: ['TypeScript', 'React', 'Node.js', 'Git', 'Agile'],
  frontend: ['React', 'TypeScript', 'CSS', 'Accessibility', 'Performance'],
  backend: ['Node.js', 'PostgreSQL', 'REST APIs', 'System Design', 'Docker'],
  product: ['Product Strategy', 'Roadmapping', 'Stakeholder Management', 'User Research', 'Agile'],
  manager: ['Team Leadership', 'Cross-functional Collaboration', 'OKRs', 'Strategic Planning'],
  data: ['SQL', 'Python', 'Data Visualization', 'Machine Learning', 'ETL'],
  design: ['Figma', 'User Research', 'Prototyping', 'Design Systems'],
  marketing: ['SEO', 'Content Strategy', 'Analytics', 'Campaign Management'],
};

export function getRecommendedSkills(
  currentSkills: string[],
  context: Pick<OnboardingDraft, 'currentRole' | 'preferredRoles' | 'industry'>,
): string[] {
  const currentLower = new Set(currentSkills.map((s) => s.toLowerCase()));
  const roleText = [context.currentRole, ...(context.preferredRoles ?? []), context.industry]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const pool: string[] = [];
  for (const [keyword, skills] of Object.entries(ROLE_SKILL_POOLS)) {
    if (roleText.includes(keyword)) pool.push(...skills);
  }
  if (pool.length === 0) {
    pool.push('Communication', 'Problem Solving', 'Agile', 'Leadership', 'Data Analysis', 'TypeScript');
  }

  return [...new Set(pool)]
    .filter((s) => !currentLower.has(s.toLowerCase()))
    .slice(0, 6);
}

export function skillsToContent(skills: string[]): string {
  return skills.join('\n');
}
